import 'react-toastify/dist/ReactToastify.css';
import './template/styles/reduction.scss';
import 'react-datepicker/dist/react-datepicker.css';
import './globals.css';

import React, { useContext } from 'react';
import { ToastContainer } from 'react-toastify';
import { BrowserRouter, Redirect, Switch, Route } from 'react-router-dom';

import { AddressProvider } from './address';
import {
  ProductCategoriesProvider,
  useProductCategories,
} from './product-categories/ProductCategoriesContext';

import {
  useFeatureFlags,
  FeatureFlagsProvider,
} from './feature-flag/FeatureFlagsContext';

import { MainLayout, EmptyLayout, LayoutRoute } from 'src/layout';
import PageSpinner from 'components/PageSpinner';

import { EntityProvider } from './entities/EntityContext';
import {
  AppConfigProvider,
  AppConfigContext,
} from './appConfig/AppConfigContext';
import { AuthContext } from './authentication/AuthContext';
import LoginPage from 'pages/LoginPage';

import ResetPasswordPage from 'pages/ResetPasswordPage';

const DashboardPage = React.lazy(() => import('pages/DashboardPage'));
const ProductsPage = React.lazy(() => import('pages/ProductsPage'));
const ProductDealerPage = React.lazy(() => import('pages/ProductDealerPage'));
const BannersPage = React.lazy(() => import('pages/BannersPage'));
const BrandsPage = React.lazy(() => import('pages/BrandsPage'));
const CompetitorProductsPage = React.lazy(() =>
  import('pages/CompetitorProductsPage'),
);
const RegionsPage = React.lazy(() => import('pages/RegionsPage'));
const ProvincesPage = React.lazy(() => import('pages/ProvincesPage'));
const DistrictsPage = React.lazy(() => import('pages/DistrictsPage'));
const SubdistrictsPage = React.lazy(() => import('pages/SubdistrictsPage'));
const PostalCodesPage = React.lazy(() => import('pages/PostalCodesPage'));
const BulkUploadPage = React.lazy(() => import('pages/BulkUploadPage'));
const DealersPage = React.lazy(() => import('pages/DealersPage'));
const ProductDealerInterfacesPage = React.lazy(() =>
  import('pages/ProductDealerInterfacePage'),
);
const SubdealersPage = React.lazy(() => import('pages/SubdealersPage'));
const ProductCategoriesPage = React.lazy(() =>
  import('pages/ProductCategoriesPage'),
);
const AdminsPage = React.lazy(() => import('pages/AdminsPage'));
const UnitsPage = React.lazy(() => import('pages/UnitsPage'));
const ProductUnitsPage = React.lazy(() => import('pages/ProductUnitsPage'));
const PricesPage = React.lazy(() => import('pages/PricesPage'));
const PlantsPage = React.lazy(() => import('pages/PlantsPage'));
const ImagesPage = React.lazy(() => import('pages/ImagesPage'));
const PaymentMethodsPage = React.lazy(() => import('pages/PaymentMethodsPage'));
const DataLoaderPage = React.lazy(() => import('pages/DataLoaderPage'));
const ProductGroupsPage = React.lazy(() => import('pages/ProductGroupsPage'));
const ShelfPage = React.lazy(() => import('pages/ShelfPage'));
const FixedShelfPage = React.lazy(() => import('pages/FixedShelfPage'));
const SegmentPage = React.lazy(() => import('pages/SegmentPage'));
const TransportationsPage = React.lazy(() =>
  import('pages/TransportationsPage'),
);
const CustomersPage = React.lazy(() => import('pages/CustomersPage'));
const SaleOrderShipCodesPage = React.lazy(() =>
  import('pages/SaleOrderShipCodesPage'),
);
const PromotionsPage = React.lazy(() => import('pages/PromotionsPage'));
const OrdersPage = React.lazy(() => import('pages/OrdersPage'));
const BundlesPage = React.lazy(() => import('pages/BundlesPage'));
const RejectReasonsPage = React.lazy(() => import('pages/RejectReasonsPage'));
const LocalizationPage = React.lazy(() => import('pages/LocalizationPage'));
const FeatureFlagPage = React.lazy(() => import('pages/FeatureFlagPage'));
const LineTemplateConfigPage = React.lazy(() =>
  import('pages/LineTemplateConfigPage'),
);
const NotificationPage = React.lazy(() => import('pages/NotificationPage'));
const LineTemplatePage = React.lazy(() => import('pages/LineTemplatePage'));
const MasterBankPage = React.lazy(() => import('pages/MasterBankPage'));
const HomeMenusPage = React.lazy(() => import('pages/HomeMenusPage'));
const ImagePopupPage = React.lazy(() => import('pages/ImagePopupPage'));
const QuotationsPage = React.lazy(() => import('pages/QuotationsPage'));
const FloatingButtonPage = React.lazy(() => import('pages/FloatingButtonPage'));
const UseForPage = React.lazy(() => import('pages/UseForPage'));
const AutoEmailPage = React.lazy(() => import('pages/AutoEmailPage'));
const LineConsolidateLogsPage = React.lazy(() =>
  import('pages/LineConsolidateLogsPage'),
);

const getBasename = () => {
  return `/${process.env.PUBLIC_URL.split('/').pop()}`;
};
const AppRoutes = (props) => {
  const { isLoaded: isLoadedProductCategories } = useProductCategories();
  const { isLoaded: isAppConfigLoaded } = useContext(AppConfigContext);
  const { isLoaded: isLoadedFeatureFlags } = useFeatureFlags();

  if (
    !isLoadedProductCategories ||
    !isAppConfigLoaded ||
    !isLoadedFeatureFlags
  ) {
    return (
      <div className="mt-5">
        <PageSpinner />
      </div>
    );
  }

  return (
    <MainLayout breakpoint={props.breakpoint}>
      <ToastContainer />
      <React.Suspense fallback={<PageSpinner />}>
        <Route exact path="/" component={DashboardPage} />
        <Route exact path="/admins" component={AdminsPage} />
        <Route exact path="/products" component={ProductsPage} />
        <Route exact path="/units" component={UnitsPage} />
        <Route exact path="/product-units" component={ProductUnitsPage} />
        <Route exact path="/plants" component={PlantsPage} />
        <Route exact path="/prices" component={PricesPage} />
        <Route exact path="/regions" component={RegionsPage} />
        <Route exact path="/provinces" component={ProvincesPage} />
        <Route exact path="/districts" component={DistrictsPage} />
        <Route exact path="/subdistricts" component={SubdistrictsPage} />
        <Route exact path="/postal-codes" component={PostalCodesPage} />
        <Route exact path="/banners" component={BannersPage} />
        <Route exact path="/bulk-upload/:entity" component={BulkUploadPage} />
        <Route
          exact
          path="/bulk-upload/:entity/:id"
          component={BulkUploadPage}
        />
        <Route exact path="/dealers" component={DealersPage} />
        <Route exact path="/sub-dealers" component={SubdealersPage} />
        <Route exact path="/customers" component={CustomersPage} />
        <Route exact path="/brands" component={BrandsPage} />
        <Route
          exact
          path="/competitor-products"
          component={CompetitorProductsPage}
        />
        <Route
          exact
          path="/product-categories"
          component={ProductCategoriesPage}
        />
        <Route exact path="/images" component={ImagesPage} />
        <Route exact path="/payment-methods" component={PaymentMethodsPage} />
        <Route exact path="/data-loader/:entity" component={DataLoaderPage} />
        <Route exact path="/product-groups" component={ProductGroupsPage} />
        <Route exact path="/shelf" component={ShelfPage} />
        <Route exact path="/fixed-shelf" component={FixedShelfPage} />
        <Route exact path="/transportations" component={TransportationsPage} />
        <Route
          exact
          path="/sale-order-ship-codes"
          component={SaleOrderShipCodesPage}
        />
        <Route exact path="/promotions" component={PromotionsPage} />
        <Route exact path="/home-menus" component={HomeMenusPage} />
        <Route exact path="/orders" component={OrdersPage} />
        <Route exact path="/quotations" component={QuotationsPage} />
        <Route
          exact
          path="/product-dealer-interfaces"
          component={ProductDealerInterfacesPage}
        />
        <Route exact path="/productDealer" component={ProductDealerPage} />
        <Route exact path="/bundles" component={BundlesPage} />
        <Route exact path="/segments" component={SegmentPage} />
        <Route exact path="/reject-reasons" component={RejectReasonsPage} />
        <Route exact path="/localization" component={LocalizationPage} />
        <Route exact path="/feature-flag" component={FeatureFlagPage} />
        <Route exact path="/line-template" component={LineTemplatePage} />
        <Route exact path="/master-bank" component={MasterBankPage} />
        <Route exact path="/image-popup" component={ImagePopupPage} />
        <Route
          exact
          path="/line-template-config"
          component={LineTemplateConfigPage}
        />
        <Route exact path="/notifications" component={NotificationPage} />
        <Route exact path="/floating-button" component={FloatingButtonPage} />
        <Route exact path="/use-for" component={UseForPage} />
        <Route exact path="/auto-email" component={AutoEmailPage} />
        <Route
          exact
          path="/line-consolidate-logs"
          component={LineConsolidateLogsPage}
        />
      </React.Suspense>
    </MainLayout>
  );
};

const App = (props) => {
  const { admin } = useContext(AuthContext);

  return (
    <>
      <EntityProvider>
        <FeatureFlagsProvider>
          <ProductCategoriesProvider>
            <AddressProvider>
              <AppConfigProvider>
                <BrowserRouter basename={getBasename()}>
                  <Switch>
                    <LayoutRoute
                      exact
                      path="/login"
                      layout={EmptyLayout}
                      component={(props) => <LoginPage {...props} />}
                    />
                    {!admin && <Redirect to="/login" />}

                    <LayoutRoute
                      exact
                      path="/reset"
                      layout={EmptyLayout}
                      component={(props) => <ResetPasswordPage {...props} />}
                    />
                    {admin?.isPasswordExpired && <Redirect to="/reset" />}

                    <AppRoutes breakpoint={props.breakpoint} />

                    <Redirect to="/" />
                  </Switch>
                </BrowserRouter>
              </AppConfigProvider>
            </AddressProvider>
          </ProductCategoriesProvider>
        </FeatureFlagsProvider>
      </EntityProvider>
    </>
  );
};

export default App;
